import { Component, Inject, NgZone, OnInit, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA as MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import firebase from 'firebase/app';
import { LogLevelsDictionary } from 'src/app/dictionaries/LogLevels';
import { UserRoles } from 'src/app/dictionaries/UserRoles';
import { LogService } from 'src/app/services/log.service';
import { Labels } from 'src/app/dictionaries/labels';

import { DialogService } from 'src/app/services/dialog.service';
import { UIMessagingService } from 'src/app/services/uimessaging.service';
import { environment } from 'src/environments/environment';
import { AuthService } from '../../services/auth.service';
import { SearchService } from '../../services/search.service';
import { WindowsManagerService } from '../../services/windows-manager.service';
import { DialogData } from '../ui/optionChooser/optionChooser.component';
import { EditClientFormComponent } from '../edit-client-form/edit-client-form.component';
import { ClientProfileComponent } from '../clientprofile/clientprofile.component';
import { UserPlans } from 'src/app/dictionaries/UserPlans';
import { FirebaseUtilitiesService } from 'src/app/services/firebase-utilities.service';

const dbPatients = firebase.firestore().collection('patients');
// Only Consultant, Client Admin, and Owner use this component
const allowedUsers = [UserRoles.admin, UserRoles.owner, UserRoles.consultant];

@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'app-spokes-search',
    styleUrls: ['./spokes-search.component.scss'],
    templateUrl: './spokes-search.component.html',
    standalone: false
})
export class SpokesSearchComponent implements OnInit {
  Labels = Labels;
  selectedOwner: any;
  ownerNeeded = false;
  nameSearch: string;
  searchResult: {
    firstname: string;
    lastname: string;
    caseName: string;
    gender: string;
    dob: string;
    patientIDs: Array<{ value: string; facility: string }>;
    hasNotifications: boolean;
  }[] = [];
  patientIdSearch: string;
  caseName: string;
  fileName: string;
  fileID: string;
  fileUploadUser: string;
  currentUser: firebase.User;
  data: any;
  userData: { realRole?: string; role?: string; user?: any; filesSharedWith?: any; caseName?: string; uid?: string };
  // Only Consultant, Client Admin, and Owner use this component
  UserRoles: { owner: string; admin: string; consultant: string };
  ownerID: any;
  originalData: any[];
  savedData: any[];
  ableToEditClient = false;
  clientsWithNotifications: any[];
  owners = [
    { value: 'owner-0', label: 'Owner 1' },
    { value: 'owner-1', label: 'Owner 2' },
    { value: 'owner-2', label: 'Owner 3' },
    { value: 'owner-3', label: 'Owner 4' },
    { value: 'owner-4', label: 'Owner 5' }
  ];
  allowedCases: any[];
  patientType: any;
  title: string;
  isSpoke: boolean;
  noHubRelationship = false;

  constructor(
    public search_$: SearchService,
    public ng: NgZone,
    private dialog_$: DialogService,
    public route: ActivatedRoute,
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<SpokesSearchComponent>,
    private log_$: LogService,
    public auth_$: AuthService,
    private uiMessaging_$: UIMessagingService,
    private windowsManager_$: WindowsManagerService,
    private firebase_utilities_$: FirebaseUtilitiesService,
    private router: Router,
    @Inject(MAT_DIALOG_DATA) public dialogdata: DialogData
  ) {
    // Only Consultant, Client Admin, and Owner use this component
    this.UserRoles = {
      owner: UserRoles.owner,
      admin: UserRoles.admin,
      consultant: UserRoles.consultant
    };
    this.data = [];
    this.nameSearch = '';
    this.patientIdSearch = '';
    this.caseName = '';
    this.fileName = '';
    this.fileID = '';
    this.fileUploadUser = '';
    this.ownerID = this.dialogdata['ownerID'];
    this.patientType = this.dialogdata['type'];
  }

  isOwner() {
    return this.auth_$.userData.getValue()['realRole'] === UserRoles.owner;
  }

  getTitle() {
    return (this.patientType === 'external' ? 'Hospital ' : 'Employee ') + this.Labels.clientMatterLabel + ' List';
  }

  async ngOnInit() {
    this.isSpoke = this.auth_$.userData.getValue()['plancode'] === UserPlans.spoke;
    this.title = this.getTitle();

    const subscription3 = this.auth_$.user.subscribe({ next: u => (this.currentUser = u) });

    const subscription = this.auth_$.userData.subscribe({
      next: async userdata => {
        const keyCount = Object.keys(userdata || {}).length;
        const ud = userdata as { realRole?: string; role?: string };
        const userRole = ud?.realRole ?? ud?.role;
        if (keyCount > 0) {
          // Assign the user data to the component
          this.userData = userdata;
          this.ableToEditClient = false;

          // Clear, explicit role handling - only Consultant, Client Admin, and Owner are supported
          const rawRole = this.userData.realRole ?? this.userData.role;
          const roleForBranch = typeof rawRole === 'string' ? rawRole.trim() : rawRole;

          if (roleForBranch === UserRoles.consultant) {
            await this.loadPatientsForConsultant();
          } else if (roleForBranch === UserRoles.admin) {
            await this.loadPatientsForClientAdmin();
          } else if (roleForBranch === UserRoles.owner || roleForBranch?.toLowerCase() === 'owner') {
            await this.loadPatientsForOwner();
          } else {
            console.warn(
              `SpokesSearchComponent: Unsupported role "${roleForBranch}". Only Consultant, Client Admin, and Owner are supported.`
            );
            this.searchResult = [];
          }

          console.log('searchResult: ', this.searchResult.length);
        }
      },
      complete: () => console.log('completed')
    });

    const subscription2 = this.search_$.searchResults.subscribe({
      next: results => this.populateSearchResults(results),
      error: err => console.error(err),
      complete: () => console.log('Completed search', this.searchResult)
    });

    return () => {
      subscription.unsubscribe();
      subscription2.unsubscribe();
      subscription3.unsubscribe();
    };
  }

  /**
   * Load patients for Consultant role.
   * Consultants see patients based on filesSharedWith and filtered by ownerID.
   */
  private async loadPatientsForConsultant() {
    this.ownerNeeded = false;

    // Get allowed case names from filesSharedWith
    this.allowedCases = Array.from(
      new Set(await this.getAllowedPatients(this.userData.realRole, this.userData.filesSharedWith, this.ownerID))
    );

    // Load patients using allowed cases with type filtering
    await this.getAllPatientsForConsultant(this.userData.user.email.toLowerCase(), this.allowedCases, this.patientType);
  }

  /**
   * Load patients for Client Admin (Facilitator) role.
   * Client Admins see patients shared with their facility via sharedWith.facilities.
   */
  private async loadPatientsForClientAdmin() {
    const facility = this.auth_$.userData.getValue()['facility'];
    if (!facility) {
      console.warn('SpokesSearchComponent: facility is missing for CLIENT_ADMIN — cannot load patients');
      this.searchResult = [];
      this.originalData = [];
      return;
    }
    // This stores the results in the originalData and searchResult arrays
    this.getAllHubsPatients(facility, this.ownerID);
  }

  /**
   * Load patients for Owner role.
   * Owners see all patients where they are the owner (ownerID match).
   * Spoke owners additionally see hub-shared patients via sharedWithFacilityMap.
   */
  private async loadPatientsForOwner() {
    this.noHubRelationship = false;
    const data = await this.getInitialData();

    if (this.isSpoke) {
      const facility = this.auth_$.userData.getValue()['facility'];
      if (facility) {
        // Merge own patients with hub-shared patients for spoke owners
        this.getAllHubsPatients(facility, this.ownerID, data);
        return;
      }
    }

    this.originalData = data;
    this.searchResult = data;
  }

  // Alternative method if array-contains doesn't work with objects
  /**
   * Load all hub patients associated with a given facility for a Client Admin (Facilitator)
   * or a Spoke Owner.
   * Filters patients whose "sharedWith.facilities" contains the specified facility.
   * Sets both originalData and searchResult arrays for search and display.
   * @param ownPatients Optional array of already-loaded own patients to merge (for spoke owners).
   */
  getAllHubsPatients(facility: string, ownerID: string, ownPatients: any[] = []) {
    const userDataFacility = this.auth_$.userData.getValue()['facility'];

    // Query by sharedWithFacilityMap — Firestore rules grant access via this field,
    // not sharedWithFacilitiesArray, so the query constraint must match the rules.
    const query = firebase
      .firestore()
      .collection('patients')
      .where(`sharedWithFacilityMap.${facility.toLowerCase()}`, '==', true);

    // Use .get() instead of .onSnapshot() — same reason as SearchComponent:
    // the Firestore Watch channel has an internal async token race condition.
    query.get().then(snapshot => {
      if (snapshot.empty && this.isSpoke) {
        this.noHubRelationship = true;
      }

      const patients = snapshot.docs
        .map(doc => {
          const patient = doc.data() || {};

          return {
            editable: patient.ownerID === ownerID,
            caseName: patient.caseName ?? '',
            origin: patient.origin ?? '',
            dob: patient.DateOfBirth ?? '',
            gender: patient.metadata?.gender ?? '',
            defaultFolders: patient.defaultFolders ?? [],
            firstname: this.toCapitalCase(patient.FirstName ?? ''),
            lastname: this.toCapitalCase(patient.LastName ?? ''),
            clioMatterId: patient?.clioMatterId ?? patient?.clioDocId ?? '',
            patientIDs: patient.metadata?.medical_record_numbers ?? [],
            name: patient.name ?? '',
            clientUser: patient.clientUser ?? '',
            LegalCaseId: patient.LegalCaseId ?? '',
            ownerID: patient.ownerID ?? '',
            owners: [patient.ownerID ?? ''],
            patientDocId: doc.id,
            sharedUserFiles: patient.sharedUserFiles ?? [],
            hasNotifications: this.hasNotifications(patient.caseName),
            restrictedProfiles: patient.restrictedProfiles ?? [],
            allowedProfiles: patient.allowedProfiles ?? [],
            type: patient.type ?? '',
            metadata: patient.metadata ?? {},
            sharedWith: patient.sharedWith ?? { facilities: [] }
          };
        })
        .filter(patient =>
          Array.isArray(patient.sharedWith?.facilities) &&
          patient.sharedWith.facilities.some(
            facilityObj => facilityObj.facility?.toLowerCase() === facility.toLowerCase()
          )
        )
        .map(patient => ({
          ...patient,
          hubs: Array.from(
            new Set(
              (patient.sharedWith.facilities || [])
                .map(facilityObj => facilityObj.hub)
                .filter(Boolean)
            )
          ).join(', '),
          patientIDs: (patient.patientIDs || []).map(pid => ({
            ...pid,
            current: userDataFacility === pid.facility
          }))
        }));

      // If ownPatients were provided (spoke owner), merge and deduplicate by patientDocId
      if (ownPatients.length > 0) {
        const ownIds = new Set(ownPatients.map(p => p.patientDocId));
        const uniqueHubPatients = patients.filter(p => !ownIds.has(p.patientDocId));
        const merged = [...ownPatients, ...uniqueHubPatients];
        this.originalData = merged;
        this.searchResult = merged;
      } else {
        this.originalData = patients;
        this.searchResult = patients;
      }
    }).catch(err => {
      console.error('SpokesSearchComponent: Patient list load error', err?.code, err?.message);
      this.originalData = ownPatients.length > 0 ? ownPatients : [];
      this.searchResult = ownPatients.length > 0 ? ownPatients : [];
    });
  }

  handleSelectClient(item) {
    if (!item.caseName) {
      console.warn('SpokesSearchComponent: No caseName found for item:', item);
      this.uiMessaging_$.toastMessage('Patient case name not found. Please contact support.', 'ALERT');
      return;
    }

    if (this.dialogRef) {
      this.dialogRef.close({ navigated: true });
    }

    // Navigate to patient overview route
    this.router
      .navigate(['/patient-overview', item.caseName], {
        skipLocationChange: false
      })
      .then(() => {
        // Close the sidebar menu after navigation completes
        if (this.auth_$.drawer) {
          this.auth_$.drawer.close();
        }
      });
  }

  editClient(item: any) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.autoFocus = true;
    const userData = this.auth_$.userData.getValue() as any;
    dialogConfig.data = {
      dob: item.dob,
      gender: item.gender,
      firstname: item.firstname,
      lastname: item.lastname,
      caseName: item.caseName,
      patientDocId: item.patientDocId,
      metadata: item.metadata,
      userFacility: userData.facility || '',
      ownerID: userData.ownerID || userData.uid,
      patientPicture: item.patientPicture || item.metadata?.patientPicture || ''
    };

    const dialogRef = this.dialog_$.open(EditClientFormComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(result => (result === 1 ? this.ngOnInit() : false));
  }

  getOwnerIdByRole(role): string {
    return role === UserRoles.owner ? this.currentUser.uid : this.auth_$.userData.getValue()['ownerID'];
  }

  private async filterAllowedPatientsByOwnerID(allowedPatients: string[], ownerID: string) {
    const patients = [];

    for (const patient of allowedPatients) {
      const res = await this.patientBelongsToOwner(patient, ownerID);
      if (res) patients.push(patient);
    }

    return patients;
  }

  private patientBelongsToOwner(patient, ownerID) {
    return firebase
      .firestore()
      .collection('patients')
      .where('caseName', '==', patient)
      .where('forDelete', '==', false)
      .where('ownerID', '==', ownerID)
      .limit(1)
      .get()
      .then(querySnapshot => {
        if (querySnapshot.empty) {
          return false;
        } else {
          return true;
        }
      });
  }

  /**
   * Get allowed patient case names based on user role.
   * @param realRole - The role of the user (Consultant, Client Admin, or Owner).
   * @param filesSharedWith - Files shared with the user (for Consultant).
   * @param ownerID - The owner ID to filter by.
   * @returns Array of allowed case names.
   */
  async getAllowedPatients(realRole: string, filesSharedWith: any, ownerID: string) {
    let allowedPatients = [];

    switch (realRole) {
      case UserRoles.consultant:
        console.log('filesSharedWith: ', filesSharedWith);
        // Get case names from filesSharedWith and filter by ownerID
        allowedPatients = [...new Set(filesSharedWith.map(currentvalue => currentvalue.patientCaseName))];
        allowedPatients = await this.filterAllowedPatientsByOwnerID(allowedPatients, ownerID);
        break;

      // Owner and Client Admin (admin) use the same logic
      case UserRoles.owner:
      case UserRoles.admin:
        // Owner and Client Admin get all patients where they are in the owners array
        const casesOwneredDocs = (await dbPatients.where('owners', 'array-contains', ownerID).get()).docs;
        casesOwneredDocs.forEach(item => allowedPatients.push(item.data().caseName));
        break;

      default:
        console.warn(`getAllowedPatients called with unsupported role: ${realRole}`);
        break;
    }
    return allowedPatients;
  }

  refreshCurrentUserSharedFiles() {
    return this.auth_$.userReady(this.auth_$.userData.getValue()['user'], 'refreshCurrentUserSharedFiles');
  }

  /**
   * Legacy method - consider using role-based load methods instead.
   * Refreshes shared files and loads patients based on role.
   */
  async getFilesSharedWith() {
    await this.refreshCurrentUserSharedFiles();
    const dbUser = this.auth_$.userData.getValue();
    const realRole = dbUser['role'];

    // Use role-based loading methods
    if (realRole === UserRoles.consultant) {
      await this.loadPatientsForConsultant();
    } else if (realRole === UserRoles.admin) {
      await this.loadPatientsForClientAdmin();
    } else if (realRole === UserRoles.owner) {
      await this.loadPatientsForOwner();
    }

    console.log('searchResult: ', this.searchResult.length);
  }

  matchSearchCriteria(item, text) {
    const criterias = ['FirstName', 'LastName', 'caseName', 'LegalCaseId', 'DateOfBirth'];
    for (const criteria of criterias) {
      if (item[criteria] && item[criteria].toLowerCase().indexOf(text.toLowerCase()) > -1) {
        return true;
      }
    }
    return false;
  }

  search(event) {
    this.searchResult = this.originalData;
    if (event.target.value && event.target.value.length > 0)
      this.searchResult = this.searchResult.filter(item => this.matchSearchCriteria(item, event.target.value));
  }

  searchRefine() {
    let filter = data => true;

    if (this.patientIdSearch.trim() !== '') {
      filter = data => data['PatientId'] === this.patientIdSearch;
    }

    if (this.nameSearch.trim() !== '') {
      filter = data => data['LastName'].toLowerCase() === this.nameSearch.toLowerCase();
    }

    this.searchResult = this.searchResult.filter(filter);
  }

  routeToProfile(patient) {
    this.log_$.storeLog(
      this.auth_$.userData.getValue(),
      LogLevelsDictionary.info,
      { message: 'A user has visited a client profile.', user: this.userData.user.uid },
      'ViewClientProfile',
      patient.caseName
    );

    if (!patient.ownerID) {
      this.uiMessaging_$.toastMessage(
        `This profile does not have a valid ownerID, please contact support before retry.`,
        'ALERT'
      );
      return;
    }

    if (!this.windowsManager_$.clientProfileOpened) {
      this.windowsManager_$.profileDialogRef = this.dialog_$.open(ClientProfileComponent, {
        width: '100vw',
        data: { patient }
      });
    }
  }

  async getInitialData() {
    if (!this.ownerID) {
      console.warn('SpokesSearchComponent: ownerID is missing, cannot load patients. Check userData/uid.');
      return [];
    }
    try {
      // Prefer owners array-contains (same as SearchComponent); fallback to ownerID for legacy docs
      let query: firebase.firestore.Query = dbPatients.where('owners', 'array-contains', this.ownerID);
      if (this.patientType) {
        query = query.where('type', '==', this.patientType);
      }
      let snapshot = await query.get();
      if (snapshot.empty) {
        // Fallback: patients may only have ownerID (no owners array)
        query = dbPatients.where('ownerID', '==', this.ownerID);
        if (this.patientType) {
          query = query.where('type', '==', this.patientType);
        }
        snapshot = await query.get();
      }
      const results = snapshot.docs
        .map(doc => this.buildClientObject2(doc.data(), doc.id))
        .sort((a, b) => (a.hasNotifications < b.hasNotifications ? 1 : -1));
      console.log(
        `SpokesSearchComponent: Loaded ${results.length} patients for ownerID=${this.ownerID}, type=${this.patientType || 'all'}`
      );
      return results;
    } catch (err) {
      console.error('SpokesSearchComponent: getInitialData failed', { ownerID: this.ownerID, patientType: this.patientType }, err);
      return [];
    }
  }

  private buildClientObject2(patient, docid) {
    return {
      caseName: patient?.caseName,
      origin: patient?.origin,
      dob: patient?.DateOfBirth,
      defaultFolders: patient?.defaultFolders,
      firstname: this.toCapitalCase(patient?.FirstName),
      lastname: this.toCapitalCase(patient?.LastName),
      clioMatterId: patient?.clioMatterId ?? patient?.clioDocId ?? '',
      name: patient?.name,
      clientUser: patient?.clientUser,
      legalCaseId: patient?.LegalCaseId,
      ownerID: patient?.ownerID,
      owners: Array.isArray(patient?.owners) ? patient.owners : (patient?.ownerID ? [patient.ownerID] : []),
      patientDocId: docid,
      sharedUserFiles: patient?.sharedUserFiles,
      hasNotifications: this.hasNotifications(patient?.caseName),
      restrictedProfiles: patient?.restrictedProfiles,
      allowedProfiles: patient?.allowedProfiles,
      type: patient?.type,
      gender: patient?.metadata?.gender,
      patientIDs: patient?.metadata?.medical_record_numbers,
      metadata: patient?.metadata ?? {}
    };
  }

  buildClientObject(patient) {
    return {
      firstname: this.toCapitalCase(patient.FirstName),
      lastname: this.toCapitalCase(patient.LastName),
      caseName: patient.caseName,
      gender: patient.metadata.gender,
      patientIDs: patient?.metadata?.medical_record_numbers,
      dob: patient.DateOfBirth,
      sharedUserFiles: patient.files,
      ownerID: patient.ownerID,
      owners: [patient.ownerID],
      defaultFolders: patient.defaultFolders,
      restrictedProfiles: patient.restrictedProfiles,
      allowedProfiles: patient.allowedProfiles,
      hasNotifications: this.hasNotifications(patient.caseName)
    };
  }

  /**
   * Get all patients for Consultant role.
   * Populates the search result (this.searchResult) based on allowed case names.
   * @param email - The email of the consultant.
   * @param allowedCaseNames - The allowed case names from filesSharedWith.
   * @param patientType - The type of the patient (external/employee).
   */
  private async getAllPatientsForConsultant(email: string, allowedCaseNames: string[], patientType: string) {
    if (!allowedCaseNames || allowedCaseNames.length === 0) {
      console.log('No cases found for consultant');
      this.searchResult = [];
      return;
    }

    const chunkSize = 10;

    // Get patients by case names, chunking if needed
    const patients = (
      allowedCaseNames.length > chunkSize
        ? await this.getPatientsByChunk(allowedCaseNames, chunkSize, patientType)
        : await this.getPatients(allowedCaseNames, patientType)
    )
      .map(doc => doc.data())
      .map(patient => this.getSearchResult(email, patient, UserRoles.consultant))
      .filter(patient => patient !== undefined); // Remove undefined results

    this.searchResult = patients;
  }

  getSearchResult(email: string, patientData: any, realrole: string) {
    const filtered = patientData.files?.filter((file: { sharedWith: string[] }) =>
      file.sharedWith.map((e: string) => e.toLowerCase()).includes(email.toLowerCase())
    );
    const condition1 = filtered?.length > 0;
    const condition2 = allowedUsers.indexOf(realrole) > -1;
    const condition3 =
      patientData.uidOfCreator === this.currentUser.uid &&
      patientData.ownerID !== '' &&
      this.ownerID === patientData.ownerID;

    if (condition1 || condition2 || condition3) return this.buildClientObject(patientData);
  }

  toCapitalCase(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  private async getPatients(allowedCaseNames: String[], type: string) {
    if (!allowedCaseNames || allowedCaseNames.length === 0) {
      return [];
    }

    const querySnapshot = await firebase
      .firestore()
      .collection('patients')
      .where('caseName', 'in', allowedCaseNames)
      .where('type', '==', type)
      .get();

    return querySnapshot.docs;
  }

  private async getPatientsByChunk(allowedCaseNames: string[], chunkSize: number, type: string) {
    let patientsArray = [];
    for (let i = 0; i < allowedCaseNames.length; i += chunkSize) {
      const chunk = allowedCaseNames.slice(i, i + chunkSize);
      const patients = await this.getPatients(chunk, type);
      patientsArray = patientsArray.concat(patients);
    }
    return patientsArray;
  }

  private hasNotifications(caseName: string): boolean {
    const userData = this.auth_$.userData.getValue();
    const isConsultant = userData['realRole'] === UserRoles.consultant;

    if (environment.config.notifications === 0) {
      return false;
    }

    let clientsWithNotifications =
      userData['notifications']?.filter(n => n.userId === userData['uid'] && n.caseId === caseName) || [];

    if (isConsultant) {
      clientsWithNotifications = clientsWithNotifications.filter(n => n.type !== 'newfile');
    }

    return clientsWithNotifications.length > 0;
  }

  private getItem(data) {
    const {
      FirstName,
      LastName,
      caseName,
      LegalCaseId,
      DateOfBirth,
      files,
      ownerID,
      defaultFolders,
      Gender,
      patientIDs
    } = data();
    return {
      firstname: this.toCapitalCase(FirstName),
      lastname: this.toCapitalCase(LastName),
      gender: Gender,
      patientIDs: patientIDs,
      caseName: caseName,
      legalCaseId: LegalCaseId,
      dob: DateOfBirth,
      sharedUserFiles: files,
      ownerID: ownerID,
      owners: [ownerID],
      defaultFolders: defaultFolders,
      hasNotifications: this.hasNotifications(caseName)
    };
  }

  private populateSearchResults(results) {
    this.searchResult = [];
    results.docs.forEach(({ data }) => {
      const item = this.getItem(data);
      const result = [];
      result.push(item);

      // Only allow if patient was created by current user and has no ownerID
      if (data().uidOfCreator === this.currentUser.uid && !item.ownerID) {
        this.searchResult.push(item);
      } else {
        console.log('No results found');
        this.searchResult = [];
      }
    });
    console.log('Search results', this.searchResult);
  }

  /**
   * Get the signed in facilitator user's facility
   * Check which hubs has shared file(s) with the signed in facilitator facility.
   * Only those hubs facilities ids will be considered to ask the patients IDs. Only fetch the patient IDs for those hubs.
   */
  async getSignedInFacilitatorFacility() {
    const userData = this.auth_$.userData.getValue();
    const facility = userData['facility'];
    return facility;
  }

  async getHubsSharedWithSignedInFacilitator(facility) {
    const hubs = await this.firebase_utilities_$.getHubsSharedWithFacility(facility);
    return hubs;
  }

  async getPatientObject(item: any) {
    // Get all the patient IDs from the item.caseName
    let patientIDs = item.patientIDs;

    // Filter only those patient IDs belong to the hubs shared with the signed in facilitator
    const facility = await this.getSignedInFacilitatorFacility();
    const hubs = await this.getHubsSharedWithSignedInFacilitator(facility);

    // Filter only those patient IDs belong to the hubs shared with the signed in facilitator and the signed in facilitator facility too, which will be the highlighter patient ID
    patientIDs = patientIDs.filter(id => hubs.includes(id.facility) || id.facility === facility);

    return {
      firstname: item.firstname,
      lastname: item.lastname,
      dob: item.dob,
      placeOfBirth: item.metadata?.placeOfBirth || '',
      spouseName: item.metadata?.spouseName || '',
      gender: item.gender || '',
      email: item.email || '',
      phone: item.phone || '',
      patientPicture: item.patientPicture || '',
      caseName: item.caseName,
      patientDocId: item.patientDocId,
      ownerID: item.ownerID,
      patientIDs: patientIDs
    };
  }

  trackByClientId(index: number, client: any): string {
    return client.patientDocId || client.caseName || index.toString();
  }
}
